/**
 * Global error handler — installs window-level listeners that surface
 * uncaught errors and unhandled promise rejections via sonner toasts.
 *
 * Stops silent failures: anything that throws on the admin side now
 * shows a red toast with the message + a "Copy" affordance so the user
 * can paste exact details into chat/support.
 */
import { toast } from 'sonner';

let installed = false;

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text).catch(() => { /* clipboard denied in kiosk shell, no-op */ });
  } catch (_) {
    /* clipboard unavailable, no-op */
  }
}

function showErrorToast(title, message, technicalDetails) {
  const fullText = `${title}\n${message}${technicalDetails ? `\n\n${technicalDetails}` : ''}`;
  toast.error(title, {
    description: message?.slice(0, 240),
    duration: 12000,
    action: {
      label: 'Copy',
      onClick: () => {
        copyToClipboard(fullText);
        toast.success('Error details copied');
      },
    },
  });
  // Always also dump to console for power users
  // eslint-disable-next-line no-console
  console.error(`[GLOBAL ERROR] ${title}: ${message}`, technicalDetails || '');
}

export function installGlobalErrorHandlers() {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (ev) => {
    const err = ev.error || {};
    showErrorToast(
      'Uncaught error',
      err.message || ev.message || 'Unknown error',
      err.stack || `${ev.filename}:${ev.lineno}:${ev.colno}`,
    );
  });

  window.addEventListener('unhandledrejection', (ev) => {
    const r = ev.reason;
    const msg = (r && (r.message || r.toString())) || String(r);
    const stack = (r && r.stack) || '';
    showErrorToast('Unhandled promise rejection', msg, stack);
  });
}

/**
 * Axios response error interceptor — adds a visible toast for any HTTP
 * error that bubbles up without explicit caller handling.
 *
 * Skips:
 *  - 401 on /auth/* (login form already shows its own message)
 *  - cancelled requests
 */
export function installAxiosErrorInterceptor(axios) {
  axios.interceptors.response.use(
    (resp) => resp,
    (error) => {
      try {
        if (axios.isCancel?.(error)) return Promise.reject(error);
        const cfg = error.config || {};
        const status = error.response?.status;
        const url = cfg.url || '';
        // Skip auth-endpoint 401s — login form handles them
        if (status === 401 && url.includes('/auth/')) return Promise.reject(error);
        // Skip endpoints that explicitly opt out via header
        if (cfg.headers?.['X-Skip-Error-Toast']) return Promise.reject(error);

        const detail =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          'Request failed';
        const technical = `${cfg.method?.toUpperCase() || 'REQ'} ${url}\nStatus: ${status || 'NETWORK'}\n${
          typeof error.response?.data === 'object'
            ? JSON.stringify(error.response.data, null, 2)
            : ''
        }`;
        showErrorToast(`API error · ${status || 'network'}`, String(detail), technical);
      } catch (_) {
        /* don't let the interceptor itself throw */
      }
      return Promise.reject(error);
    },
  );
}
